/* The bar across the top of every page: the name, the section tabs, the collection switcher,
   a status line, whatever buttons the page hands in, and at the far end the user: signed in,
   their name, which opens a menu of two (Profile, Account); signed out, the way to the account
   page. The theme is not chosen here: that is the account page's, where there is room for it. */

import type { Collection } from "@preflop/api";
import { el, fill } from "./dom";
import { DONE } from "./marks";

const SECTIONS = [
  { id: "ranges", label: "Ranges", href: "./" },
  { id: "practice", label: "Practice", href: "./practice.html" },
  { id: "stats", label: "Stats", href: "./stats.html" },
  { id: "collections", label: "Collections", href: "./collections.html" },
] as const;
/** The profile, account and privacy pages have the bar but no tab: two are reached from the
    menu at the far end, the third from the foot of the account page. */
export type Section =
  (typeof SECTIONS)[number]["id"] | "profile" | "account" | "privacy" | "moderation";

const MANAGE = "manage";

export interface Topbar {
  root: HTMLElement;
  /** The quiet line beside the switcher: how much is loaded, and from where. */
  setState(text: string): void;
  /** Who is signed in: their name becomes the button of the user menu. Until it is known, and
      signed out (""), the bar has the plain "Account" link instead. */
  setAccount(name: string): void;
  /** Fill the switcher. `onSwitch` resolves to false when the page would not let go (unsaved
      text), and the switcher goes back to what it said. Hidden until this is called: a page
      with no session has no collections to offer. */
  setCollections(
    list: readonly Collection[],
    activeId: string,
    /** Whether a collection is the user's own. One that is neither theirs nor the base is a
        public one they chose in the gallery, and is listed apart. */
    mine: (c: Collection) => boolean,
    onSwitch: (to: Collection) => Promise<boolean>,
  ): void;
  /** For administrators: a third item in the user menu, with the number of collections whose
      reports are waiting. Null takes it away; it is not there until this is called. */
  setModeration(waiting: number | null): void;
  /** Where today stands against the daily goal, and the streak; null takes it away (no goal,
      or nothing known). Hidden until this is called. */
  setDaily(daily: DailyPill | null): void;
}

export interface DailyPill {
  goal: number;
  /** Hands answered today. */
  today: number;
  met: boolean;
  /** Consecutive days the goal was met. */
  current: number;
}

export function topbar(current: Section, ...actions: HTMLElement[]): Topbar {
  const state = el("span", { class: "state", id: "sessionState" });
  const account = el(
    "a",
    {
      class: "copy account",
      href: "./account.html",
      title: "Your account",
      dataset: { testid: "account-link" },
      ...(current === "account" ? { ariaCurrent: "page" as const } : {}),
    },
    "Account",
  );
  /* The user menu. Two links under a button, so it is built by hand: a native select cannot
     hold links or a separator that means anything. What a menu owes the keyboard is here:
     arrows, Home and End between the items, Escape back to the button, and gone as soon as
     focus is. */
  const item = (
    id: "profile" | "account" | "moderation",
    label: string,
    href: string,
    title: string,
  ): HTMLAnchorElement =>
    el(
      "a",
      {
        role: "menuitem",
        href,
        title,
        tabIndex: -1,
        dataset: { testid: `menu-${id}` },
        ...(current === id ? { ariaCurrent: "page" as const } : {}),
      },
      label,
    );
  const moderation = item(
    "moderation",
    "Moderation",
    "./moderation.html",
    "Reported collections: you are an administrator",
  );
  const moderationRule = el("hr", { role: "separator", hidden: true });
  moderation.hidden = true;
  const every = [
    item(
      "profile",
      "Profile",
      "./profile.html",
      "Your preferences: the theme, what you call the seats, your daily goal",
    ),
    item("account", "Account", "./account.html", "Your name, password and account"),
    moderation,
  ];
  /** The items that are there: the keyboard walks these, and not one that is hidden. */
  const items = {
    at: (i: number): HTMLAnchorElement | undefined => every.filter((x) => !x.hidden).at(i),
    indexOf: (x: HTMLAnchorElement): number => every.filter((y) => !y.hidden).indexOf(x),
    get length(): number {
      return every.filter((x) => !x.hidden).length;
    },
  };
  const menu = el(
    "div",
    { class: "menu", role: "menu", ariaLabel: "You", hidden: true },
    every[0],
    el("hr", { role: "separator" }),
    every[1],
    moderationRule,
    moderation,
  );
  const who = el("button", {
    type: "button",
    class: "copy account",
    hidden: true,
    ariaHasPopup: "menu",
    ariaExpanded: "false",
    dataset: { testid: "user-menu" },
  });
  const open = (focus?: "first" | "last"): void => {
    menu.hidden = false;
    who.ariaExpanded = "true";
    if (focus) items.at(focus === "first" ? 0 : -1)?.focus();
  };
  const close = (): void => {
    menu.hidden = true;
    who.ariaExpanded = "false";
  };
  who.onclick = () => (menu.hidden ? open() : close());
  who.onkeydown = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    open(e.key === "ArrowDown" ? "first" : "last");
  };
  menu.onkeydown = (e) => {
    const at = items.indexOf(document.activeElement as HTMLAnchorElement);
    const to =
      e.key === "ArrowDown"
        ? (at + 1) % items.length
        : e.key === "ArrowUp"
          ? (at - 1 + items.length) % items.length
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? items.length - 1
              : -1;
    if (to < 0) return;
    e.preventDefault();
    items.at(to)?.focus();
  };
  const user = el("div", { class: "user" }, account, who, menu);

  // Today against the goal, and the streak: a way to the stats page that says why to go.
  const daily = el("a", {
    class: "daily",
    href: "./stats.html",
    hidden: true,
    dataset: { testid: "daily" },
  });
  user.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || menu.hidden) return;
    close();
    who.focus();
  });
  // Focus gone elsewhere, or a click anywhere else: a menu nobody is in is closed.
  user.addEventListener("focusout", (e) => {
    if (!(e.relatedTarget instanceof Node) || !user.contains(e.relatedTarget)) close();
  });
  addEventListener("click", (e) => {
    if (!(e.target instanceof Node) || !user.contains(e.target)) close();
  });

  // Native on purpose: complete for the keyboard and a screen reader as it comes, and the
  // platform's own picker on a phone.
  const switcher = el("select", {
    class: "switcher",
    title: "The collection the viewer shows and the trainer drills",
    ariaLabel: "Active collection",
    hidden: true,
    dataset: { testid: "collection-switcher" },
  });

  const root = el(
    "header",
    { class: "topbar" },
    // The logo and the name are one link home. The image says nothing the name beside it does
    // not, so it is empty for a screen reader. 64px drawn at 22: sharp on a dense screen.
    el(
      "a",
      { class: "brand", href: "./", title: "Preflop Ranges" },
      el("img", {
        class: "logo",
        src: "/icons/icon-64.png",
        alt: "",
        width: 22,
        height: 22,
        dataset: { testid: "logo" },
      }),
      el("h1", {}, "Preflop"),
    ),
    el(
      "nav",
      { class: "tabs", ariaLabel: "Sections" },
      SECTIONS.map((s) =>
        el(
          "a",
          {
            class: "tab" + (s.id === current ? " on" : ""),
            href: s.href,
            ...(s.id === current ? { ariaCurrent: "page" } : {}),
          },
          s.label,
        ),
      ),
    ),
    switcher,
    state,
    el("span", { class: "spacer" }),
    daily,
    ...actions,
    user,
  );

  return {
    root,
    setState: (text) => void (state.textContent = text),
    // As text, like every name a user wrote.
    setAccount(name) {
      who.textContent = name;
      who.hidden = !name;
      account.hidden = !!name;
      if (!name) close();
    },

    setModeration(waiting) {
      moderation.hidden = moderationRule.hidden = waiting === null;
      fill(
        moderation,
        "Moderation",
        waiting
          ? el("span", { class: "count", dataset: { testid: "moderation-count" } }, String(waiting))
          : null,
      );
    },

    setDaily(d) {
      daily.hidden = !d;
      if (!d) return;
      const days = `${d.current} ${d.current === 1 ? "day" : "days"}`;
      daily.classList.toggle("met", d.met);
      daily.title = daily.ariaLabel =
        `Today: ${d.today} of ${d.goal} hands${d.met ? ", goal met" : ""}. ` +
        (d.current ? `Streak: ${days} in a row.` : "No streak yet.");
      fill(
        daily,
        d.current > 0 && el("span", { class: "streak" }, `\u{1F525} ${d.current}`),
        d.current > 0 && el("span", { class: "dot" }, "·"),
        el("span", {}, `${d.today} / ${d.goal}` + (d.met ? ` ${DONE}` : "")),
      );
    },

    setCollections(list, activeId, isMine, onSwitch) {
      const mine = list.filter((c) => !c.isBase && isMine(c));
      const chosen = list.filter((c) => !c.isBase && !isMine(c));
      const option = (c: Collection): HTMLOptionElement =>
        el("option", { value: c.id, selected: c.id === activeId }, c.name);
      fill(
        switcher,
        list.filter((c) => c.isBase).map(option),
        mine.length ? el("optgroup", { label: "Yours" }, mine.map(option)) : null,
        chosen.length ? el("optgroup", { label: "Public" }, chosen.map(option)) : null,
        el("optgroup", { label: "—" }, el("option", { value: MANAGE }, "Manage collections…")),
      );
      switcher.value = activeId;
      switcher.hidden = false;
      switcher.onchange = () => {
        if (switcher.value === MANAGE) {
          switcher.value = activeId;
          location.assign("./collections.html");
          return;
        }
        const to = list.find((c) => c.id === switcher.value);
        if (!to || to.id === activeId) return;
        switcher.disabled = true;
        void onSwitch(to)
          .catch(() => false)
          .then((left) => {
            if (left) return;
            switcher.value = activeId;
            switcher.disabled = false;
          });
      };
    },
  };
}
